import { useEffect, type ReactNode } from "react";
import { useCookieConsent } from "../lib/cookieConsent";
import { setPosthogInstance } from "../lib/analytics";

const POSTHOG_KEY = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;
const POSTHOG_HOST = import.meta.env.VITE_PUBLIC_POSTHOG_HOST || "https://us.i.posthog.com";

let posthogLoaded = false;

/**
 * Wraps children with PostHog tracking, gated by cookie consent.
 * PostHog JS is dynamically imported only after the user consents, which keeps
 * it out of the initial bundle and off entirely until opt-in (Law 25).
 */
export default function PostHogProvider({ children }: { children: ReactNode }) {
  const { consented } = useCookieConsent();

  useEffect(() => {
    if (!consented || !POSTHOG_KEY || posthogLoaded) return;

    let cancelled = false;

    import("posthog-js").then(({ default: posthog }) => {
      if (cancelled) return;

      posthog.init(POSTHOG_KEY, {
        api_host: POSTHOG_HOST,
        defaults: "2026-01-30",
        capture_pageview: true,
        autocapture: true,
      });

      setPosthogInstance(posthog);
      posthogLoaded = true;
    });

    return () => { cancelled = true; };
  }, [consented]);

  return <>{children}</>;
}
